import type { GridColDef } from '@mui/x-data-grid'
import { CheckSettingsRow, MultiEditCheckSettingsElement } from '../MultiEditCheckSettings/types'

/**
 * True when the member check settings carry a steel support config, i.e. the
 * member is a steel column (or rip). Timber members and "other" members do not.
 */
export const hasSteelBasePlateSettings = (
  settings: SettingsOnMember | undefined,
): settings is SteelColumnCheckSettings => settings?.setting_type === 'steel-column'

export const getSteelBasePlateSettings = (
  settings: SettingsOnMember | undefined,
): SteelSupportConfig | undefined =>
  settings && 'steel_support_config' in settings ? settings.steel_support_config : undefined

/**
 * The geometry/strength fields the Fußpunkt form edits. element_guid and
 * member_guid identify which member and supporting element the config belongs
 * to — they must never be copied from one member's config to another's, so
 * they are excluded from both the equality check and the merge.
 */
type EditableSteelSupportConfig = Omit<SteelSupportConfig, 'element_guid' | 'member_guid'>

const pickEditableFields = (config: SteelSupportConfig): EditableSteelSupportConfig => {
  const { element_guid: _elementGuid, member_guid: _memberGuid, ...editable } = config
  return editable
}

export const getEditableSteelBasePlateSettings = (
  settings: SettingsOnMember | undefined,
): EditableSteelSupportConfig | undefined => {
  const config = getSteelBasePlateSettings(settings)
  return config ? pickEditableFields(config) : undefined
}

/**
 * The staged settings are always seeded from a member that already passed
 * {@link hasSteelBasePlateSettings} (see SharedSettingsFields), so a populated
 * steel_support_config is guaranteed here. As with vibration check settings,
 * there is no safe default to synthesize for a base plate geometry or design
 * resistance invented out of thin air, so this is an identity function kept
 * for interface parity with the other sections.
 */
export const normalizeSteelBasePlateSettings = (settings: SettingsOnMember): SettingsOnMember =>
  settings

/**
 * Applies the staged shared geometry/strength fields to one member, keeping its
 * own element_guid/member_guid and the rest of its check settings untouched.
 */
export const mergeSteelBasePlateSettings = (
  existing: SettingsOnMember,
  shared: SettingsOnMember,
): SettingsOnMember => {
  const existingConfig = getSteelBasePlateSettings(existing)
  const sharedConfig = getSteelBasePlateSettings(shared)

  if (!existingConfig || !sharedConfig) return existing

  return {
    ...existing,
    steel_support_config: { ...existingConfig, ...pickEditableFields(sharedConfig) },
  } as SettingsOnMember
}

export const steelBasePlateSettingsColumns: GridColDef<CheckSettingsRow>[] = [
  { field: 'thickness', headerName: 'Dicke', flex: 1, minWidth: 70 },
  { field: 'length', headerName: 'Länge', flex: 1, minWidth: 70 },
  { field: 'width', headerName: 'Breite', flex: 1, minWidth: 70 },
  { field: 'materialStrength', headerName: 'Widerstand', flex: 1, minWidth: 90 },
  { field: 'weldThickness', headerName: 'Schweißnaht', flex: 1, minWidth: 100 },
]

const millimeterFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 })
const materialStrengthFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 })

/** base_plate_geometry / weld_thickness are stored in meters, like everywhere else in the model. */
const formatMillimeters = (meters: number): string =>
  `${millimeterFormatter.format(meters * 1000)} mm`

/** design_support_material_strength is stored in Pascal, matching UnitConvertedField's conversionFactor. */
const formatMaterialStrength = (pascals: number): string =>
  `${materialStrengthFormatter.format(pascals / 1_000_000)} N/mm²`

const EMPTY_ROW: Record<string, string> = {
  thickness: '–',
  length: '–',
  width: '–',
  materialStrength: '–',
  weldThickness: '–',
}

/** While a shared value is staged it is previewed for every row. */
export const buildSteelBasePlateSettingsRow = (
  element: MultiEditCheckSettingsElement,
  sharedSettings: SettingsOnMember | undefined,
): Record<string, string> => {
  const config =
    getSteelBasePlateSettings(sharedSettings) ?? getSteelBasePlateSettings(element.checkSettings)

  if (!config) return EMPTY_ROW

  return {
    thickness: formatMillimeters(config.base_plate_geometry.thickness),
    length: formatMillimeters(config.base_plate_geometry.length),
    width: formatMillimeters(config.base_plate_geometry.width),
    materialStrength: formatMaterialStrength(config.design_support_material_strength),
    weldThickness: formatMillimeters(config.weld_thickness),
  }
}
